import LegalLayout from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="February 2026" testid="privacy-page">
      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">The short version</h2>
        <p>
          Lyric Lock is a personal practice tool. We collect the minimum we need to run it,
          we never sell your data, and we don't show ads. Your songs and practice history are
          for your eyes.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">What we collect</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong className="text-foreground">Account info</strong> — your email address, your
            name (optional), and a securely hashed password.
          </li>
          <li>
            <strong className="text-foreground">Song content</strong> — the songs you add:
            title, artist, lyrics, section structure, and any backing track audio you upload.
          </li>
          <li>
            <strong className="text-foreground">Practice data</strong> — the accuracy of your
            drills, mastery scores, streaks, XP, achievements, and the words the app has learned
            you tend to miss.
          </li>
          <li>
            <strong className="text-foreground">Mic recordings</strong> — when you use "Sing It"
            mode, your short recording is sent to our AI transcription provider so the app can
            grade it, then discarded. We do not store the audio on our servers.
          </li>
          <li>
            <strong className="text-foreground">Basic technical logs</strong> — standard server
            logs (IP, timestamp, error info) kept briefly for reliability and abuse prevention.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">How we use it</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Show your songs back to you and power the practice modes.</li>
          <li>Track progress so spaced repetition schedules the right song at the right time.</li>
          <li>Generate hints, memory maps, and voice grading through AI providers.</li>
          <li>Keep the service stable and safe.</li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Who we share it with</h2>
        <p>
          We share small, purpose-limited pieces of content with a few trusted providers so
          specific features can work:
        </p>
        <ul className="list-disc pl-6 mt-3 space-y-1">
          <li>
            <strong className="text-foreground">Google Gemini</strong> — text of a section (and
            your list of tricky words) when you request an AI hint or Memory Map analysis.
          </li>
          <li>
            <strong className="text-foreground">OpenAI Whisper</strong> — the short mic recording
            when you use "Sing It", to transcribe what you sang.
          </li>
          <li>
            <strong className="text-foreground">LRCLIB</strong> — the search terms you type when
            looking up lyrics; nothing about your account is sent.
          </li>
          <li>
            <strong className="text-foreground">Our hosting provider</strong> — to run the
            servers and store the database.
          </li>
        </ul>
        <p className="mt-3">
          We do not sell your personal information to anyone, ever.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Cookies</h2>
        <p>
          We use a single essential cookie called <span className="font-mono text-sm">ll_token</span>
          {" "}to keep you signed in. It's HTTP-only, secure, and only sent to Lyric Lock.
          We do not use tracking or advertising cookies.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Your choices</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Sign out any time from the header — the sign-in cookie is cleared.</li>
          <li>Delete a song and it's gone from your library along with its practice history.</li>
          <li>
            Ask us to delete your account and all associated data by contacting us through the
            channel on the home page. We'll do it as soon as reasonably possible.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Security</h2>
        <p>
          Passwords are hashed with bcrypt. Traffic is encrypted in transit. We follow standard
          practices to keep your data safe, but no service is perfectly secure. If something
          goes wrong, we'll let affected users know.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Children</h2>
        <p>
          Lyric Lock is not intended for children under 13. If we learn we've collected data
          from someone under 13, we'll delete it.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Changes</h2>
        <p>
          If we update this policy, the "Last updated" date at the top will change and we'll
          highlight material changes in the app.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Contact</h2>
        <p>
          For privacy questions or a data request, reach us through the contact channel on the
          home page.
        </p>
      </section>
    </LegalLayout>
  );
}
